import { useEffect, useMemo } from 'react';

import type { ErrorLike } from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client';
import { skipToken } from '@apollo/client/react';
import type {
  SellerTransactionQuery_sellerProducts_SellerProductsQuery_sellerTransaction_InContractTransaction as InContractTransaction,
  SellerTransactionQuery_sellerProducts_SellerProductsQuery_sellerTransaction_InitializingTransaction as InitializingTransaction,
  SellerTransactionQuery_sellerProducts_SellerProductsQuery_sellerTransaction_QualifiedTransaction as QualifiedTransaction,
  SellerTransactionQuery_sellerProducts_SellerProductsQuery_sellerTransaction as SellerTransaction,
} from '__generated__/graphql';
import { SellerTransactionDocument } from '__generated__/graphql';
import type { GraphQLFormattedError } from 'graphql';
import { useAuthenticatedQuery } from 'platform/apollo';
import { useAuthContext } from 'platform/components/AuthContext';
import { useObservability } from 'platform/observability';
import { useParams } from 'react-router';
import { extractSellerLead } from 'sellDirect/declarations/sellerTransaction';

export type {
  SellerTransaction,
  InitializingTransaction,
  QualifiedTransaction,
  InContractTransaction,
};

const SELLER_TRANSACTION_PATH = ['sellerProducts', 'sellerTransaction'] as const;

/** Fetches the seller transaction for a property address */
export const useSellerTransaction = () => {
  const { customerUUID } = useAuthContext();
  const { addressUUID } = useParams();
  const { trackError } = useObservability();

  const { data, loading, error, refetch, startPolling, stopPolling } = useAuthenticatedQuery(
    SellerTransactionDocument,
    !addressUUID
      ? skipToken
      : {
          variables: {
            addressUUID,
            ...(customerUUID !== undefined ? { customerUUID } : {}),
          },
          fetchPolicy: 'network-only',
          context: { batch: false },
        },
  );

  const sellerTransaction = data?.sellerProducts?.sellerTransaction;
  const lead = useMemo(() => extractSellerLead(sellerTransaction), [sellerTransaction]);

  const { terminalError, nestedFieldErrors } = useMemo(
    () => partitionSellerTransactionError(error, sellerTransaction != null),
    [error, sellerTransaction],
  );

  useEffect(() => {
    for (const err of nestedFieldErrors) {
      // eslint-disable-next-line @opendoor/track-error-prefer-throw -- Nested nullable field errors are reported without blocking the loaded transaction.
      trackError('seller-transaction-nested-field-error', undefined, {
        path: err.path?.join('.'),
        message: err.message,
        code: err.extensions?.code,
        sellerLeadUuid: lead?.id,
        addressUUID,
        customerUUID,
      });
    }
  }, [nestedFieldErrors, trackError, addressUUID, lead?.id, customerUUID]);

  return useMemo(
    () => ({
      sellerTransaction,
      lead,
      loading,
      error: terminalError,
      refetch,
      startPolling,
      stopPolling,
    }),
    [terminalError, loading, refetch, sellerTransaction, lead, startPolling, stopPolling],
  );
};

type PartitionedSellerTransactionError = {
  /**
   * Errors that prevented the SellerTransaction from being constructed:
   * network/protocol failures, or GraphQL errors that resulted in a null transaction.
   */
  terminalError: ErrorLike | undefined;
  /** Errors from nullable fields inside the transaction. Report, don't surface. */
  nestedFieldErrors: readonly GraphQLFormattedError[];
};

/**
 * Relies on GraphQL's null-propagation rule: when `sellerTransaction` comes
 * back populated, every error must be from a nested nullable field, anything
 * required would have bubbled the parent to null.
 */
export const partitionSellerTransactionError = (
  error: ErrorLike | undefined,
  sellerTransactionLoaded: boolean,
): PartitionedSellerTransactionError => {
  if (!error) {
    return { terminalError: undefined, nestedFieldErrors: [] };
  }

  // Non-GraphQL errors (network/parse/protocol) are always terminal
  if (!CombinedGraphQLErrors.is(error)) {
    return { terminalError: error, nestedFieldErrors: [] };
  }

  // The transaction failed to construct, so every GraphQL error contributed
  if (!sellerTransactionLoaded) {
    return { terminalError: error, nestedFieldErrors: [] };
  }

  const nested: GraphQLFormattedError[] = [];
  const unrecognized: GraphQLFormattedError[] = [];
  for (const gqlError of error.errors) {
    (isErrorInsideSellerTransaction(gqlError) ? nested : unrecognized).push(gqlError);
  }

  if (unrecognized.length > 0) {
    return { terminalError: error, nestedFieldErrors: [] };
  }

  return { terminalError: undefined, nestedFieldErrors: nested };
};

/**
 * Returns true when the error's path is strictly inside the transaction.
 * Path filtering is defense-in-depth for unrelated sibling errors if the query ever grows.
 */
export const isErrorInsideSellerTransaction = (err: GraphQLFormattedError): boolean => {
  const path = err.path;
  if (!path || path.length <= SELLER_TRANSACTION_PATH.length) return false;
  return SELLER_TRANSACTION_PATH.every((segment, i) => path[i] === segment);
};
